import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './GrantDocuments.css';
import { PROJECT_ROLES, normalizeProjectRole, isSamePerson } from '../utils/keyPersonnel';

// Same personnel fields the Upload Documents page collects for a biosketch. Stored
// per grant, per subcontractor site as custom metadata on the uploaded file.
const EMPTY_BIOSKETCH = {
  firstName: '',
  middleName: '',
  lastName: '',
  positionTitle: '',
  organizationName: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  country: '',
  zipCode: '',
  phoneNumber: '',
  email: '',
  eraCommonsUsername: '',
  projectRole: ''
};

const BIOSKETCH_DOC_ID = 5;

const SubcontractorDocumentsManagement = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subcontractorSites, setSubcontractorSites] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  // Keyed "siteIndex:docId" so only the control being used shows a busy state.
  const [uploadingKey, setUploadingKey] = useState(null);
  // Notes typed for an Additional Document, keyed by site index.
  const [additionalNotes, setAdditionalNotes] = useState({});
  const fileInputRefs = useRef({});

  // Biosketch entry mirrors the Upload Documents page: a personnel form plus the file.
  // Only one site's form is open at a time; biosketchFormSite holds that site index.
  const [biosketchFormSite, setBiosketchFormSite] = useState(null);
  const [biosketchFormData, setBiosketchFormData] = useState(EMPTY_BIOSKETCH);
  const [biosketchUploading, setBiosketchUploading] = useState(false);
  const biosketchFileInputRef = useRef(null);
  const [biosketchFileName, setBiosketchFileName] = useState('');
  // Stored filename of the biosketch being edited; null when adding a new one.
  const [editingBiosketchFile, setEditingBiosketchFile] = useState(null);
  // Name on the biosketch before the edit, so the questionnaire entry can be found
  // even when the name itself is changed.
  const [editingBiosketchOriginal, setEditingBiosketchOriginal] = useState(null);
  // Sites whose required-document list is collapsed, so the list of sites stays scannable.
  const [collapsedSites, setCollapsedSites] = useState(new Set());

  // Same download behaviour as the Grant Documents page: fetch the blob so the
  // file saves under its own name instead of opening in a tab.
  const handleFileDownload = async (fileUrl, filename) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleBiosketchFormChange = (e) => {
    const { name, value } = e.target;
    setBiosketchFormData(prev => ({ ...prev, [name]: value }));
  };

  const closeBiosketchForm = () => {
    setBiosketchFormSite(null);
    setBiosketchFormData(EMPTY_BIOSKETCH);
    setBiosketchFileName('');
    setEditingBiosketchFile(null);
    setEditingBiosketchOriginal(null);
    if (biosketchFileInputRef.current) biosketchFileInputRef.current.value = '';
  };

  const toggleSiteCollapsed = (siteIndex) => {
    setCollapsedSites(prev => {
      const next = new Set(prev);
      if (next.has(siteIndex)) {
        next.delete(siteIndex);
      } else {
        next.add(siteIndex);
      }
      return next;
    });
  };

  // Senior/key personnel the questionnaire listed against a given organization.
  // Each of them needs a biosketch at that sub-contract site.
  const keyPersonnelForSite = (site) => {
    const people = application?.questionnaire?.answers?.keyPersonnel;
    if (!Array.isArray(people)) return [];
    const org = String(site?.organizationName || '').trim();
    if (!org) return [];
    return people.filter(person =>
      String(person.organizationName || '').trim() === org
      && (String(person.firstName || '').trim() || String(person.lastName || '').trim())
    );
  };

  // Start the biosketch form from what the questionnaire already knows, so the
  // two pages hold the same details for each person. The business address is
  // left blank: the sub-contractor fills in their own organization's address
  // (the performance site address is not their business address).
  const openBiosketchFormForKeyPerson = (actualSiteIndex, person, site) => {
    setEditingBiosketchFile(null);
    setEditingBiosketchOriginal(null);
    setBiosketchFormData({
      ...EMPTY_BIOSKETCH,
      firstName: person.firstName || '',
      lastName: person.lastName || '',
      positionTitle: person.positionTitle || '',
      organizationName: site?.organizationName || person.organizationName || '',
      country: 'UNITED STATES',
      eraCommonsUsername: person.eraCommonsUsername || '',
      projectRole: normalizeProjectRole(person.projectRole)
    });
    setBiosketchFileName('');
    setBiosketchFormSite(actualSiteIndex);
  };

  // A blank form for a key person the questionnaire doesn't list yet; saving it
  // adds them to the questionnaire's Senior/Key Personnel.
  const openNewBiosketchForm = (actualSiteIndex, site) => {
    setEditingBiosketchFile(null);
    setEditingBiosketchOriginal(null);
    setBiosketchFormData({
      ...EMPTY_BIOSKETCH,
      organizationName: site?.organizationName || '',
      country: 'UNITED STATES'
    });
    setBiosketchFileName('');
    setBiosketchFormSite(actualSiteIndex);
  };

  // Open the personnel form pre-filled with an uploaded biosketch's details.
  const openEditBiosketchForm = (actualSiteIndex, file) => {
    setEditingBiosketchFile(file.originalFilename);
    setEditingBiosketchOriginal({
      firstName: file.biosketch?.firstName || '',
      lastName: file.biosketch?.lastName || ''
    });
    setBiosketchFormData({
      ...EMPTY_BIOSKETCH,
      ...(file.biosketch || {}),
      projectRole: normalizeProjectRole(file.biosketch?.projectRole)
    });
    setBiosketchFileName(file.filename || '');
    setBiosketchFormSite(actualSiteIndex);
  };

  // Re-read the application after a biosketch save, since the backend mirrors
  // biosketches into the questionnaire's key personnel.
  const refreshApplication = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setApplication(data.application);
      }
    } catch (err) {
      console.error('Error refreshing application:', err);
    }
  };

  // Fetch application details and subcontractor document status
  useEffect(() => {
    const fetchApplicationDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch application details');
        }

        const data = await response.json();
        setApplication(data.application);

        // Extract subcontractor sites (all sites except the primary site)
        if (data.application.questionnaire?.answers?.projectSites) {
          const sites = data.application.questionnaire.answers.projectSites;
          const subcontractors = sites.filter((site, index) => index > 0); // Exclude primary site (index 0)
          setSubcontractorSites(subcontractors);
        }

        // Fetch subcontractor files directly from GCS
        await fetchSubcontractorFiles();
      } catch (error) {
        console.error('Error fetching application:', error);
        setError('Failed to load application details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (applicationId) {
      fetchApplicationDetails();
    }
  }, [applicationId]);

  const fetchSubcontractorFiles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/subcontract/application/${applicationId}/files`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUploadedDocuments(data.filesBySite || {});
      }
    } catch (error) {
      console.error('Error fetching subcontractor files:', error);
      // Don't show error to user, just log it
    }
  };

  // The Statement of Intent and Scope of Work are submitted together, so they sit
  // next to each other with a note saying so.
  const CONSORTIUM_NOTE = 'The Subrecipient Statement of Intent and Scope of Work will be combined and submitted under "Consortium/Contractual Arrangements".';
  const requiredSubcontractorDocuments = [
    { id: 1, name: 'Subrecipient Statement of Intent', description: CONSORTIUM_NOTE },
    { id: 4, name: 'Scope of Work', description: CONSORTIUM_NOTE },
    { id: 2, name: 'Sub-contract Budget' },
    { id: 3, name: 'Sub-contract Budget Justification' },
    // Biosketches: one per key person, entered through the same personnel form as the
    // Upload Documents page, so any number of files may be uploaded.
    {
      id: BIOSKETCH_DOC_ID,
      name: 'Biosketch of Key Personnel',
      description:
        'SBIR: The PI must be primarily employed (more than 50% time) by the applicant small business. '
        + 'Faculty will usually be Co-I.\n'
        + 'STTR: The PI may be employed by the applicant small business or the partnering non-profit '
        + 'research institution.\n'
        + 'If there is more than one PI, a Multiple PI Leadership Plan must be provided by the applicant '
        + 'small business.',
      isMultiUpload: true,
      isBiosketch: true
    },
    { id: 6, name: 'Facilities and Other Resources' },
    { id: 7, name: 'Major Equipment' },
    {
      id: 8,
      name: 'Letters of Support',
      description: 'Please combine all letters of support into a single pdf.',
      isOptional: true
    },
    {
      id: 9,
      name: 'Additional Documents',
      description: 'Anything else needed for this site. Add a note describing each file.',
      isOptional: true,
      isMultiUpload: true,
      hasNotes: true
    }
  ];

  // Only the numbered documents count toward the "uploaded" progress figure.
  const requiredDocCount = requiredSubcontractorDocuments.filter(d => !d.isOptional).length;

  // Upload a file for a subcontract site as the logged-in user. Writes to the same
  // GCS location as the emailed token-based upload page.
  const handleSiteFileUpload = async (actualSiteIndex, doc, file, biosketch = null) => {
    if (!file) return;
    const key = `${actualSiteIndex}:${doc.id}`;
    const notes = doc.hasNotes ? (additionalNotes[actualSiteIndex] || '') : '';

    try {
      setUploadingKey(key);

      // Multi-upload documents get a timestamp so a second file doesn't overwrite the first.
      const storedFilename = doc.isMultiUpload
        ? `doc_${doc.id}_${Date.now()}_${file.name}`
        : `doc_${doc.id}_${file.name}`;

      const urlResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/subcontract/application/${applicationId}/site/${actualSiteIndex}/upload-url`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: storedFilename,
            contentType: file.type
          })
        }
      );

      if (!urlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const urlData = await urlResponse.json();

      // Only Content-Type here: any custom header would trigger a CORS preflight the
      // bucket rejects. Notes / biosketch details are attached server-side below.
      const uploadResponse = await fetch(urlData.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      if (notes || biosketch) {
        const confirmResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/subcontract/application/${applicationId}/site/${actualSiteIndex}/confirm-upload`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename: storedFilename, notes, biosketch })
          }
        );

        if (!confirmResponse.ok) {
          throw new Error('File uploaded but its details could not be saved');
        }
      }

      await fetchSubcontractorFiles();
      if (doc.hasNotes) {
        setAdditionalNotes(prev => ({ ...prev, [actualSiteIndex]: '' }));
      }
    } catch (err) {
      console.error('Error uploading subcontractor document:', err);
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploadingKey(null);
      const ref = fileInputRefs.current[key];
      if (ref) ref.value = '';
    }
  };

  const handleDeleteSiteFile = async (actualSiteIndex, storedFilename) => {
    if (!window.confirm('Remove this file?')) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/subcontract/application/${applicationId}/site/${actualSiteIndex}/file`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: storedFilename })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      await fetchSubcontractorFiles();
    } catch (err) {
      console.error('Error deleting subcontractor document:', err);
      alert(`Delete failed: ${err.message}`);
    }
  };

  const REQUIRED_BIOSKETCH_FIELDS = [
    'firstName', 'lastName', 'positionTitle', 'organizationName',
    'street1', 'city', 'state', 'zipCode', 'country', 'phoneNumber', 'email'
  ];

  // Save edits to an already-uploaded biosketch. Re-uses the confirm-upload endpoint,
  // which writes the personnel details onto the stored file's metadata.
  const handleBiosketchEditSave = async (actualSiteIndex) => {
    const missing = REQUIRED_BIOSKETCH_FIELDS.filter(
      f => !biosketchFormData[f] || String(biosketchFormData[f]).trim() === ''
    );
    if (missing.length > 0) {
      alert('Please fill in all required fields before saving.');
      return;
    }

    setBiosketchUploading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/subcontract/application/${applicationId}/site/${actualSiteIndex}/confirm-upload`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: editingBiosketchFile,
            biosketch: biosketchFormData,
            match: editingBiosketchOriginal
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save biosketch details');
      }

      await Promise.all([fetchSubcontractorFiles(), refreshApplication()]);
      closeBiosketchForm();
    } catch (err) {
      console.error('Error updating subcontract biosketch:', err);
      alert(`Failed to save biosketch details: ${err.message}`);
    } finally {
      setBiosketchUploading(false);
    }
  };

  // Submit the biosketch personnel form together with the selected file.
  const handleBiosketchSubmit = async (actualSiteIndex) => {
    // Editing an existing entry only rewrites its details, not the file.
    if (editingBiosketchFile) {
      return handleBiosketchEditSave(actualSiteIndex);
    }

    const file = biosketchFileInputRef.current?.files?.[0];

    if (!file) {
      alert('Please select a biosketch file to upload');
      return;
    }

    const missing = REQUIRED_BIOSKETCH_FIELDS.filter(
      f => !biosketchFormData[f] || String(biosketchFormData[f]).trim() === ''
    );
    if (missing.length > 0) {
      alert('Please fill in all required fields before uploading.');
      return;
    }

    const extension = file.name.toLowerCase().split('.').pop();
    if (!['pdf', 'doc', 'docx'].includes(extension)) {
      alert('Please select a PDF or Word document for the biosketch.');
      return;
    }

    const personnelName = `${biosketchFormData.lastName}_${biosketchFormData.firstName}`.replace(/[^a-zA-Z0-9]/g, '_');
    const renamed = new File([file], `${personnelName}_${file.name}`, { type: file.type });

    setBiosketchUploading(true);
    try {
      await handleSiteFileUpload(
        actualSiteIndex,
        { id: BIOSKETCH_DOC_ID, isMultiUpload: true },
        renamed,
        biosketchFormData
      );
      await refreshApplication();
      closeBiosketchForm();
    } finally {
      setBiosketchUploading(false);
    }
  };

  const handleReturn = () => {
    navigate('/grant-submission');
  };

  const handleResendEmail = async (siteIndex) => {
    try {
      const site = subcontractorSites[siteIndex];
      // Newer questionnaires store the sub-contract PI separately; older drafts only
      // have the single legacy contact email.
      const contactEmail = site.subcontractPIEmail || site.subcontractContactEmail;
      const ccEmails = site.subcontractBusinessOfficeEmail ? [site.subcontractBusinessOfficeEmail] : [];

      const tokenResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/subcontract/generate-token`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          applicationId,
          siteIndex: siteIndex + 1, // +1 because we're excluding the primary site (index 0)
          contactEmail,
          contactName: site.subcontractPIName || '',
          siteName: site.organizationName,
          piName: application.questionnaire?.answers?.piName || '',
          ccEmails
          // companyName is intentionally omitted: the backend resolves it from the
          // session's company context and looks up the official name for display.
        })
      });

      if (tokenResponse.ok) {
        alert('Email resent successfully!');
      } else {
        alert('Failed to resend email');
      }
    } catch (error) {
      console.error('Error resending email:', error);
      alert('Failed to resend email');
    }
  };

  if (isLoading) {
    return <div className="loading-indicator">Loading subcontractor documents...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="grant-documents-container">
      <div className="documents-header">
        <h1>Subcontractor Documents Management</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="return-btn"
            onClick={() => navigate(`/grant-submission/documents/${applicationId}`)}
            style={{ backgroundColor: '#6b7280' }}
          >
            Back to Documents
          </button>
          <button className="return-btn" onClick={handleReturn}>
            Return to Applications
          </button>
        </div>
      </div>

      <div className="application-info-bar">
        <h2>{application?.name}</h2>
        <p style={{ marginTop: '8px', color: '#6b7280' }}>
          Track and manage documents uploaded by subcontractors
        </p>
      </div>

      {subcontractorSites.length === 0 ? (
        <div className="card p-6 text-center">
          <p style={{ color: '#6b7280' }}>
            No subcontractor sites have been added to this grant application.
          </p>
          <p style={{ marginTop: '8px', fontSize: '14px', color: '#9ca3af' }}>
            Add subcontractor sites in the grant questionnaire to manage their document uploads here.
          </p>
        </div>
      ) : (
        <div className="documents-list">
          {subcontractorSites.map((site, siteIndex) => {
            const actualSiteIndex = siteIndex + 1; // Adjust for primary site being index 0
            const siteDocuments = uploadedDocuments[actualSiteIndex] || [];
            // Count distinct required documents that have at least one file.
            const uploadedCount = requiredSubcontractorDocuments
              .filter(d => !d.isOptional && siteDocuments.some(f => f.docId === d.id))
              .length;
            const totalRequired = requiredDocCount;
            const isSiteCollapsed = collapsedSites.has(actualSiteIndex);

            return (
              <div key={siteIndex} className="card" style={{ marginBottom: '24px' }}>
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f9fafb',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                        {site.organizationName || `Subcontractor Site #${siteIndex + 1}`}
                      </h3>
                      <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                        {(site.subcontractPIEmail || site.subcontractContactEmail) ? (
                          <>
                            Contact: {site.subcontractPIName ? `${site.subcontractPIName} — ` : ''}
                            {site.subcontractPIEmail || site.subcontractContactEmail}
                            {site.subcontractBusinessOfficeEmail ? ` (CC: ${site.subcontractBusinessOfficeEmail})` : ''}
                          </>
                        ) : (
                          <span style={{ color: '#ef4444' }}>No contact email provided</span>
                        )}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: '24px',
                        fontWeight: '600',
                        color: uploadedCount === totalRequired ? '#10b981' : '#f59e0b'
                      }}>
                        {uploadedCount}/{totalRequired}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        Documents Uploaded
                      </div>
                    </div>
                  </div>

                  {(site.subcontractPIEmail || site.subcontractContactEmail) && (
                    <button
                      onClick={() => handleResendEmail(siteIndex)}
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Resend Upload Link Email
                    </button>
                  )}
                </div>

                <div style={{ padding: '20px' }}>
                  <button
                    type="button"
                    onClick={() => toggleSiteCollapsed(actualSiteIndex)}
                    aria-expanded={!isSiteCollapsed}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      margin: '0 0 16px 0',
                      padding: 0,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <span style={{
                      display: 'inline-block',
                      transition: 'transform 0.2s',
                      transform: isSiteCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                      fontSize: '12px',
                      color: '#6b7280'
                    }}>
                      ▼
                    </span>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                      Required Documents
                    </h4>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      ({uploadedCount}/{totalRequired} uploaded{isSiteCollapsed ? ' — click to expand' : ''})
                    </span>
                  </button>
                  <div style={{ display: isSiteCollapsed ? 'none' : 'grid', gap: '12px' }}>
                    {requiredSubcontractorDocuments.map(doc => {
                      const docFiles = siteDocuments.filter(f => f.docId === doc.id);
                      const isUploaded = docFiles.length > 0;
                      const uploadKey = `${actualSiteIndex}:${doc.id}`;
                      const isUploading = uploadingKey === uploadKey;

                      const primaryFile = docFiles[0];

                      return (
                        <div key={doc.id} className="document-card">
                          {/* Same header markup and button order as the Grant Documents
                              page, so a document row reads identically on both. */}
                          <div className="document-header">
                            <div className="document-title-block">
                              <h4>{doc.name}</h4>
                              {isUploaded && !doc.isMultiUpload && (
                                <span className="document-meta">
                                  {[
                                    primaryFile.filename,
                                    primaryFile.uploadDate
                                      ? new Date(primaryFile.uploadDate).toLocaleDateString()
                                      : null
                                  ].filter(Boolean).join(' \u00b7 ')}
                                </span>
                              )}
                              {doc.description && (
                                <span className="document-meta" style={{ whiteSpace: 'pre-line' }}>
                                  {doc.description}
                                </span>
                              )}
                            </div>

                            <div className="document-header-actions">
                              <span className={`status-indicator ${isUploaded ? 'uploaded' : (doc.isOptional ? 'optional' : 'missing')}`}>
                                {isUploaded
                                  ? `Uploaded${doc.isMultiUpload && docFiles.length > 1 ? ` (${docFiles.length})` : ''}`
                                  : (doc.isOptional ? 'Optional' : 'Required')}
                              </span>

                              <input
                                type="file"
                                style={{ display: 'none' }}
                                ref={el => { fileInputRefs.current[uploadKey] = el; }}
                                onChange={(e) => handleSiteFileUpload(actualSiteIndex, doc, e.target.files?.[0])}
                              />

                              {doc.isBiosketch ? null : isUploaded && !doc.isMultiUpload ? (
                                <div className="file-actions">
                                  {primaryFile.downloadUrl && (
                                    <>
                                      <button
                                        type="button"
                                        className="view-btn"
                                        onClick={() => window.open(primaryFile.downloadUrl, '_blank')}
                                      >
                                        View
                                      </button>
                                      <button
                                        type="button"
                                        className="download-btn"
                                        onClick={() => handleFileDownload(primaryFile.downloadUrl, primaryFile.filename)}
                                      >
                                        Download
                                      </button>
                                    </>
                                  )}
                                  <button
                                    type="button"
                                    className="replace-btn"
                                    disabled={isUploading}
                                    onClick={() => fileInputRefs.current[uploadKey]?.click()}
                                  >
                                    {isUploading ? 'Uploading...' : 'Replace'}
                                  </button>
                                  <button
                                    type="button"
                                    className="remove-btn"
                                    onClick={() => handleDeleteSiteFile(actualSiteIndex, primaryFile.originalFilename)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="upload-btn-inline"
                                  onClick={() => fileInputRefs.current[uploadKey]?.click()}
                                  disabled={isUploading}
                                  style={{
                                    height: '28px',
                                    padding: '0 12px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    lineHeight: 1,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isUploading ? 'not-allowed' : 'pointer',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {isUploading
                                    ? 'Uploading...'
                                    : doc.isMultiUpload
                                      ? '+ Add File'
                                      : 'Upload'}
                                </button>
                              )}
                            </div>
                          </div>

                          {doc.hasNotes && (
                            <div style={{ marginTop: '10px' }}>
                              <label
                                htmlFor={`notes-${uploadKey}`}
                                style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}
                              >
                                Upload Name, Title, or Description:
                              </label>
                              <textarea
                                id={`notes-${uploadKey}`}
                                value={additionalNotes[actualSiteIndex] || ''}
                                onChange={(e) => setAdditionalNotes(prev => ({ ...prev, [actualSiteIndex]: e.target.value }))}
                                placeholder="Describe the document before adding the file"
                                rows={2}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '13px' }}
                              />
                            </div>
                          )}

                          {/* Personnel biosketch form — mirrors the Upload Documents page */}
                          {doc.isBiosketch && biosketchFormSite === actualSiteIndex && (
                            <div className="biosketch-upload-form" style={{ marginTop: '12px' }}>
                              <div className="biosketch-header">
                                <h5>{editingBiosketchFile ? 'Edit Personnel Biosketch' : 'Add Personnel Biosketch'}</h5>
                              </div>

                              <div className="form-row">
                                <div className="form-group required">
                                  <label htmlFor={`bs-firstName-${actualSiteIndex}`}>First Name:</label>
                                  <input type="text" id={`bs-firstName-${actualSiteIndex}`} name="firstName"
                                    value={biosketchFormData.firstName} onChange={handleBiosketchFormChange} required />
                                </div>
                                <div className="form-group">
                                  <label htmlFor={`bs-middleName-${actualSiteIndex}`}>Middle Name:</label>
                                  <input type="text" id={`bs-middleName-${actualSiteIndex}`} name="middleName"
                                    value={biosketchFormData.middleName} onChange={handleBiosketchFormChange} />
                                </div>
                                <div className="form-group required">
                                  <label htmlFor={`bs-lastName-${actualSiteIndex}`}>Last Name:</label>
                                  <input type="text" id={`bs-lastName-${actualSiteIndex}`} name="lastName"
                                    value={biosketchFormData.lastName} onChange={handleBiosketchFormChange} required />
                                </div>
                              </div>

                              <div className="form-row">
                                <div className="form-group required">
                                  <label htmlFor={`bs-positionTitle-${actualSiteIndex}`}>Position/Title:</label>
                                  <input type="text" id={`bs-positionTitle-${actualSiteIndex}`} name="positionTitle"
                                    value={biosketchFormData.positionTitle} onChange={handleBiosketchFormChange} required />
                                </div>
                                <div className="form-group required">
                                  <label htmlFor={`bs-organizationName-${actualSiteIndex}`}>Organization Name:</label>
                                  <input type="text" id={`bs-organizationName-${actualSiteIndex}`} name="organizationName"
                                    value={biosketchFormData.organizationName} onChange={handleBiosketchFormChange} required />
                                </div>
                              </div>

                              <div className="address-section">
                                <h6>Business Address</h6>
                                <div className="form-group required">
                                  <label htmlFor={`bs-street1-${actualSiteIndex}`}>Street 1:</label>
                                  <input type="text" id={`bs-street1-${actualSiteIndex}`} name="street1"
                                    value={biosketchFormData.street1} onChange={handleBiosketchFormChange} required />
                                </div>
                                <div className="form-group">
                                  <label htmlFor={`bs-street2-${actualSiteIndex}`}>Street 2:</label>
                                  <input type="text" id={`bs-street2-${actualSiteIndex}`} name="street2"
                                    value={biosketchFormData.street2} onChange={handleBiosketchFormChange} />
                                </div>
                                <div className="form-row">
                                  <div className="form-group required">
                                    <label htmlFor={`bs-city-${actualSiteIndex}`}>City:</label>
                                    <input type="text" id={`bs-city-${actualSiteIndex}`} name="city"
                                      value={biosketchFormData.city} onChange={handleBiosketchFormChange} required />
                                  </div>
                                  <div className="form-group required">
                                    <label htmlFor={`bs-state-${actualSiteIndex}`}>State:</label>
                                    <input type="text" id={`bs-state-${actualSiteIndex}`} name="state"
                                      value={biosketchFormData.state} onChange={handleBiosketchFormChange} required />
                                  </div>
                                  <div className="form-group required">
                                    <label htmlFor={`bs-zipCode-${actualSiteIndex}`}>Zip/Postal Code:</label>
                                    <input type="text" id={`bs-zipCode-${actualSiteIndex}`} name="zipCode"
                                      value={biosketchFormData.zipCode} onChange={handleBiosketchFormChange} required />
                                  </div>
                                  <div className="form-group required">
                                    <label htmlFor={`bs-country-${actualSiteIndex}`}>Country:</label>
                                    <input type="text" id={`bs-country-${actualSiteIndex}`} name="country"
                                      value={biosketchFormData.country} onChange={handleBiosketchFormChange} required />
                                  </div>
                                </div>
                              </div>

                              <div className="form-row">
                                <div className="form-group required">
                                  <label htmlFor={`bs-phoneNumber-${actualSiteIndex}`}>Phone Number:</label>
                                  <input type="tel" id={`bs-phoneNumber-${actualSiteIndex}`} name="phoneNumber"
                                    value={biosketchFormData.phoneNumber} onChange={handleBiosketchFormChange} required />
                                </div>
                                <div className="form-group required">
                                  <label htmlFor={`bs-email-${actualSiteIndex}`}>Email:</label>
                                  <input type="email" id={`bs-email-${actualSiteIndex}`} name="email"
                                    value={biosketchFormData.email} onChange={handleBiosketchFormChange} required />
                                </div>
                              </div>

                              <div className="form-row">
                                <div className="form-group">
                                  <label htmlFor={`bs-era-${actualSiteIndex}`}>eRA Commons Username:</label>
                                  <input type="text" id={`bs-era-${actualSiteIndex}`} name="eraCommonsUsername"
                                    value={biosketchFormData.eraCommonsUsername} onChange={handleBiosketchFormChange} />
                                </div>
                                <div className="form-group required">
                                  <label htmlFor={`bs-projectRole-${actualSiteIndex}`}>Project Role:</label>
                                  <select id={`bs-projectRole-${actualSiteIndex}`} name="projectRole"
                                    value={biosketchFormData.projectRole} onChange={handleBiosketchFormChange} required>
                                    <option value="">-- Select --</option>
                                    {PROJECT_ROLES.map(role => (
                                      <option key={role} value={role}>{role}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {editingBiosketchFile ? (
                                <div className="form-group biosketch-file">
                                  <label>Biosketch File:</label>
                                  <span className="file-name">{biosketchFileName || 'Already uploaded'}</span>
                                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                                    To swap the file itself, remove this biosketch and upload it again.
                                  </p>
                                </div>
                              ) : (
                                <div className="form-group biosketch-file required">
                                  <label>Biosketch File:</label>
                                  <div className="file-upload-button">
                                    <button
                                      type="button"
                                      className="upload-file-btn"
                                      onClick={() => biosketchFileInputRef.current?.click()}
                                    >
                                      Select File
                                    </button>
                                    <span className="file-name">{biosketchFileName || 'No file selected'}</span>
                                  </div>
                                </div>
                              )}

                              <input
                                type="file"
                                ref={biosketchFileInputRef}
                                style={{ display: 'none' }}
                                onChange={(e) => setBiosketchFileName(e.target.files?.[0]?.name || '')}
                              />

                              <div className="biosketch-form-actions">
                                <button
                                  type="button"
                                  className="submit-btn"
                                  onClick={() => handleBiosketchSubmit(actualSiteIndex)}
                                  disabled={
                                    biosketchUploading ||
                                    !biosketchFormData.firstName ||
                                    !biosketchFormData.lastName ||
                                    (!editingBiosketchFile && !biosketchFileName)
                                  }
                                >
                                  {editingBiosketchFile
                                    ? (biosketchUploading ? 'Saving...' : 'Save Changes')
                                    : (biosketchUploading ? 'Uploading...' : 'Upload Biosketch')}
                                </button>
                                <button type="button" className="cancel-btn" onClick={closeBiosketchForm}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}

                          {/* One biosketch per senior/key person the questionnaire
                              listed at this organization. */}
                          {doc.isBiosketch && biosketchFormSite !== actualSiteIndex && (() => {
                            const outstanding = keyPersonnelForSite(site).filter(person =>
                              !docFiles.some(f => isSamePerson(f.biosketch || {}, person))
                            );
                            if (outstanding.length === 0) return null;
                            return (
                              <div className="biosketches-list" style={{ marginTop: '10px' }}>
                                <h5>Biosketch still needed</h5>
                                {outstanding.map((person, i) => (
                                  <div key={i} className="biosketch-item">
                                    <div className="biosketch-info">
                                      <span className="personnel-name">
                                        {`${person.firstName || ''} ${person.lastName || ''}`.trim()}
                                      </span>
                                      <span className="biosketch-meta">
                                        {person.projectRole || 'From the questionnaire'}
                                      </span>
                                    </div>
                                    <div className="biosketch-actions">
                                      <button
                                        className="edit-btn small"
                                        onClick={() => openBiosketchFormForKeyPerson(actualSiteIndex, person, site)}
                                      >
                                        Add Biosketch
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}

                          {/* Biosketches use the same card markup/classes as the Upload
                              Documents page so both pages render identically. */}
                          {doc.isBiosketch && docFiles.length > 0 && (
                            <div className="biosketches-list" style={{ marginTop: '10px' }}>
                              {docFiles.map((f, i) => {
                                const bio = f.biosketch || {};
                                return (
                                  <div key={i} className="biosketch-item">
                                    <div className="biosketch-info">
                                      <span className="personnel-name">
                                        {bio.firstName || bio.lastName
                                          ? `${bio.firstName || ''} ${bio.middleName ? `${bio.middleName} ` : ''}${bio.lastName || ''}`.trim()
                                          : 'Unknown'}
                                      </span>
                                      <span className="position-title">{bio.positionTitle || 'No title provided'}</span>
                                      <span className="organization">{bio.organizationName || 'No organization provided'}</span>
                                      <span className="project-role">Role: {bio.projectRole || 'Not specified'}</span>
                                      {!keyPersonnelForSite(site).some(person => isSamePerson(person, bio)) && (
                                        <span className="biosketch-detail" style={{ color: '#b45309' }}>
                                          Name doesn't match any Senior/Key Personnel listed for this organization in
                                          the questionnaire. Edit and save to add/update them there.
                                        </span>
                                      )}
                                      <span className="biosketch-detail">
                                        {[
                                          bio.street1,
                                          bio.street2,
                                          [bio.city, bio.state].filter(Boolean).join(', '),
                                          bio.zipCode,
                                          bio.country
                                        ].filter(Boolean).join(' · ') || 'No address provided'}
                                      </span>
                                      <span className="biosketch-detail">
                                        {[
                                          bio.phoneNumber,
                                          bio.email,
                                          bio.eraCommonsUsername ? `eRA: ${bio.eraCommonsUsername}` : null
                                        ].filter(Boolean).join(' · ') || 'No contact details provided'}
                                      </span>
                                      <span className="filename">{f.filename}</span>
                                      {f.uploadDate && (
                                        <span className="upload-date">
                                          Uploaded: {new Date(f.uploadDate).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>

                                    <div className="biosketch-actions">
                                      <button
                                        className="edit-btn small"
                                        onClick={() => openEditBiosketchForm(actualSiteIndex, f)}
                                      >
                                        Edit
                                      </button>
                                      {f.downloadUrl && (
                                        <>
                                          <button
                                            className="view-btn small"
                                            onClick={() => window.open(f.downloadUrl, '_blank')}
                                          >
                                            View
                                          </button>
                                          <button
                                            className="download-btn small"
                                            onClick={() => handleFileDownload(f.downloadUrl, f.filename)}
                                          >
                                            Download
                                          </button>
                                        </>
                                      )}
                                      <button
                                        className="remove-btn small"
                                        onClick={() => handleDeleteSiteFile(actualSiteIndex, f.originalFilename)}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {doc.isBiosketch && biosketchFormSite !== actualSiteIndex && (
                            <button
                              type="button"
                              className="add-biosketch-btn"
                              style={{ marginTop: '10px' }}
                              onClick={() => openNewBiosketchForm(actualSiteIndex, site)}
                            >
                              + Add Additional Key Personnel
                            </button>
                          )}

                          {/* Same compact card markup the Grant Documents Upload page
                              uses, so uploaded files read identically on both pages. */}
                          {!doc.isBiosketch && doc.isMultiUpload && docFiles.length > 0 && (
                            <div className="biosketches-list">
                              <h5>Uploaded Documents</h5>
                              {docFiles.map((f, i) => (
                                <div key={i} className="biosketch-item">
                                  <div className="biosketch-info">
                                    <span className="personnel-name">
                                      {f.notes || f.filename}
                                    </span>
                                    <span className="biosketch-meta">
                                      {[
                                        f.notes ? f.filename : null,
                                        f.uploadDate ? new Date(f.uploadDate).toLocaleDateString() : null
                                      ].filter(Boolean).join(' \u00b7 ')}
                                    </span>
                                  </div>
                                  <div className="biosketch-actions">
                                    {f.downloadUrl && (
                                      <>
                                        <button
                                          className="view-btn small"
                                          onClick={() => window.open(f.downloadUrl, '_blank')}
                                        >
                                          View
                                        </button>
                                        <button
                                          className="download-btn small"
                                          onClick={() => handleFileDownload(f.downloadUrl, f.filename)}
                                        >
                                          Download
                                        </button>
                                      </>
                                    )}
                                    <button
                                      className="remove-btn small"
                                      onClick={() => handleDeleteSiteFile(actualSiteIndex, f.originalFilename)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Files that don't map to a known document slot (e.g. uploaded under an
                      older naming scheme) would otherwise be invisible. */}
                  {siteDocuments.filter(f => !requiredSubcontractorDocuments.some(d => d.id === f.docId)).length > 0 && (
                    <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                      <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>
                        Other Uploaded Files
                      </h5>
                      <div className="biosketches-list">
                        {siteDocuments.filter(f => !requiredSubcontractorDocuments.some(d => d.id === f.docId)).map((doc, idx) => (
                          <div key={idx} className="biosketch-item">
                            <div className="biosketch-info">
                              <span className="personnel-name">{doc.filename}</span>
                              {doc.uploadDate && (
                                <span className="biosketch-meta">
                                  {new Date(doc.uploadDate).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            {(doc.downloadUrl || doc.fileUrl) && (
                              <div className="biosketch-actions">
                                <button
                                  className="view-btn small"
                                  onClick={() => window.open(doc.downloadUrl || doc.fileUrl, '_blank')}
                                >
                                  View
                                </button>
                                <button
                                  className="download-btn small"
                                  onClick={() => handleFileDownload(doc.downloadUrl || doc.fileUrl, doc.filename)}
                                >
                                  Download
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubcontractorDocumentsManagement;
